import authConfig from '@app/services/Auth/authConfig';
import LocalStorage, { LOCAL_STORAGE_EVENT } from '@app/utils/LocalStorage';
import { Auth0Provider } from '@auth0/auth0-react';
import { AuthProvider as PropelAuthProvider } from '@propelauth/react';

import React, { useRef, useSyncExternalStore } from 'react';
import history from '../../utils/history';
import {
	defaultPropelAuthBridgeValue,
	PropelAuthBridge,
	PropelAuthBridgeContext,
} from './PropelAuthBridge';

type AuthProviderProps = {
	children: React.ReactNode;
};

const AUTH_PROVIDER_STORAGE_KEY = 'checkAuthProvider';

// Subscribe to both cross-tab (`storage`) and same-tab (`LOCAL_STORAGE_EVENT`)
// changes to `checkAuthProvider`. The native `storage` event never fires in the
// tab that made the write, so the custom event (dispatched by the LocalStorage
// util) is what makes same-tab provider switches reactive.
function subscribeToAuthProvider(onChange: () => void) {
	window.addEventListener('storage', onChange);
	window.addEventListener(LOCAL_STORAGE_EVENT, onChange);
	return () => {
		window.removeEventListener('storage', onChange);
		window.removeEventListener(LOCAL_STORAGE_EVENT, onChange);
	};
}

function getSelectedAuthProviderSnapshot() {
	return LocalStorage.getFromLocalStorage(AUTH_PROVIDER_STORAGE_KEY);
}

export default function AuthProvider(props: AuthProviderProps) {
	const { children } = props;

	const onRedirectCallback = (appState: any) => {
		history.push(
			appState && appState.targetUrl
				? appState.targetUrl
				: window.location.pathname + '/home' + window.location.search,
		);
	};

	// PropelAuth's SDK hardcodes a background /api/v1/refresh_token poll (on
	// mount, every 60s, and on window focus/online) the moment its provider
	// mounts — with no prop to disable it. So we only mount it when PropelAuth is
	// (or may still become) the active provider; Auth0/niche users never mount it
	// and never see the polling.
	//
	// This is read reactively (useSyncExternalStore) rather than once at boot,
	// because `checkAuthProvider` is written mid-session with no page reload — by
	// the email-check reducer and the axios 401 interceptor's LocalStorage.clear()
	// — and a stale boot-time read would leave PropelAuth unmounted exactly when a
	// user needs to log in with it, silently no-oping the login.
	const selectedAuthProviderName = useSyncExternalStore(
		subscribeToAuthProvider,
		getSelectedAuthProviderSnapshot,
	);
	const propelAuthNeeded =
		!selectedAuthProviderName || selectedAuthProviderName === 'propelAuth';

	// Latch: once PropelAuth has been mounted, keep it mounted for the rest of the
	// page's life. Flipping this ternary swaps the element type at this position
	// and would remount the entire `children` subtree (<Routes /> and all app
	// state below it). The enable direction (→ propelAuth/none) only happens at
	// logged-out/login boundaries (fresh load, logout, post-401 clear), where a
	// remount is harmless. The disable direction (→ auth0/niche) can happen while
	// a fresh visitor's email resolves to auth0 mid-login; latching prevents that
	// from remounting and dropping the login screen's transient state. A returning
	// auth0/niche user boots with the latch already false, so they still never
	// mount PropelAuth.
	const hasMountedPropelAuthRef = useRef(propelAuthNeeded);
	if (propelAuthNeeded) {
		hasMountedPropelAuthRef.current = true;
	}
	const isPropelAuthEnabled = hasMountedPropelAuthRef.current;

	return (
		<Auth0Provider
			useRefreshTokens={true} // this automatically adds the `offline_access` scope
			cacheLocation="localstorage"
			domain={authConfig?.auth0.domain || ''}
			clientId={authConfig?.auth0.clientId || ''}
			redirectUri={authConfig?.redirectUri}
			onRedirectCallback={onRedirectCallback}
			audience={authConfig?.auth0.audience}
			scope={authConfig?.auth0.scope}
		>
			{isPropelAuthEnabled ? (
				<PropelAuthProvider authUrl={authConfig?.propelAuth.authUrl}>
					<PropelAuthBridge>{children}</PropelAuthBridge>
				</PropelAuthProvider>
			) : (
				<PropelAuthBridgeContext.Provider value={defaultPropelAuthBridgeValue}>
					{children}
				</PropelAuthBridgeContext.Provider>
			)}
		</Auth0Provider>
	);
}
